import ipaddr from 'ipaddr.js';
import { z } from 'zod';
import { ipv4Regex, ipv6Regex } from '#schemas/general/helpers/ip-address.ts';
import { stringToList } from '#screens/hosts/form/advanced/mappers/stringToList.ts';
import { reformatCommaList } from './helpers/list.ts';

export const RESOLVER_FORMAT_ERR = 'All entries must be bare IP addresses with no port (e.g. 100.100.0.20)';
export const RESOLVER_ZONE_ERR = 'All entries must be bare IP addresses with no zone suffix';
export const RESOLVER_UNICAST_ERR = 'All entries must be unicast IP addresses';
export const RESOLVER_LINK_LOCAL_ERR = 'IPv6 link-local addresses are not supported';
export const RESOLVER_DOTTED_QUAD_ERR = 'IPv4 addresses must use dotted-quad form (e.g. 100.100.0.20)';
export const RESOLVER_EMPTY_ERR = 'Enter a comma-separated list of IP addresses';

/**
 * Validates one resolver address, mirroring the api's `definednet.dns.resolver_addrs` validator.
 * Returns an error message, or `null` when the address is acceptable.
 *
 * OS resolver config (resolv.conf, NRPT, /etc/resolver files) cannot express ports or zones, so
 * only bare unicast IPs are accepted. Loopback is allowed — a local stub resolver is a legitimate
 * setup — but IPv6 link-local can't select an interface without a zone, and IPv4-mapped IPv6
 * literals don't survive the round trip into OS resolver config on every platform.
 */
export function validateResolverAddr(value: string): string | null {
  // ip-regex accepts a zone suffix and ipaddr.js parses one, so reject it explicitly.
  if (value.includes('%')) {
    return RESOLVER_ZONE_ERR;
  }

  // ipaddr.js is looser than the api's `netip.ParseAddr` (it accepts `1.1.1`, `010.1.1.1`,
  // `2001:db8::00001`, `::ffff:001.2.3.4`), so gate on the regexes first and only use ipaddr.js to
  // classify what they already accepted. ipRegex agreed with netip on every input we probed.
  if (!ipv4Regex.test(value) && !ipv6Regex.test(value)) {
    return RESOLVER_FORMAT_ERR;
  }

  const addr = ipaddr.parse(value);
  const range = addr.range();

  // Unspecified and multicast addresses parse but cannot name a resolver (glibc treats 0.0.0.0 in
  // resolv.conf as the local host). Compare bits rather than `range()`, which reports every address
  // in 0.0.0.0/8 as unspecified while netip matches only 0.0.0.0 and :: exactly.
  if (addr.toByteArray().every((byte) => byte === 0) || range === 'multicast') {
    return RESOLVER_UNICAST_ERR;
  }

  if (addr instanceof ipaddr.IPv6) {
    // ipaddr.js also rewrites the deprecated IPv4-compatible form (::1.2.3.4) to ::ffff:1.2.3.4,
    // which netip reads as a plain IPv6 address, so only reject the literal mapped spelling.
    if (addr.isIPv4MappedAddress() && value.toLowerCase().includes('ffff:')) {
      return RESOLVER_DOTTED_QUAD_ERR;
    }
    // IPv4 link-local needs no zone and is reachable over the on-link route: AWS serves DNS at
    // 169.254.169.253. IPv6 link-local has no such escape hatch.
    if (range === 'linkLocal') {
      return RESOLVER_LINK_LOCAL_ERR;
    }
  }

  return null;
}

const resolverAddrListSchema = z.string().superRefine((value, ctx) => {
  for (const addr of stringToList(value)) {
    const message = validateResolverAddr(addr);
    if (message) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, fatal: true });
      return z.NEVER;
    }
  }
});

export const dnsResolverAddrsSchema = z.string().transform(reformatCommaList).pipe(resolverAddrListSchema);

export const dnsResolverAddrsRequiredSchema = dnsResolverAddrsSchema.refine((value) => value.length !== 0, {
  message: RESOLVER_EMPTY_ERR,
});
